import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Eye, FilePlus2, Loader2, Printer } from "lucide-react";
import { Button } from "@akuna/ui";
import { Autocomplete, type AutocompleteOption } from "@/components/ui/autocomplete";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import {
  useCreatePatientTerm,
  useMarkTermPrinted,
  useTermTemplates,
  useUploadTermSignedFile,
  type PatientTerm,
} from "@/hooks/usePatientTerms";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { RichTextEditor } from "@/components/terms/RichTextEditor";
import { buildSignatureTokenValues, normalizeTermTemplateConfig, renderTermWithTokenValues } from "@/lib/terms/config";
import { useSignPdf } from "@/hooks/useSignPdf";
import { useUserA1Certificate } from "@/hooks/useUserA1Certificate";
import { useIsVeterinarian } from "@/hooks/useIsVeterinarian";
import { downloadPdf, printPdf } from "@/lib/pdf/download";
import { useRenderPdf } from "@/hooks/useRenderPdf";
import { sanitizeRichTextHtml } from "@/components/terms/rich-text-utils";
import { cn } from "@/lib/utils";
import { useDocumentTypes } from "@/hooks/useLookups";
import { useTranslation } from "react-i18next";
import { usePrintSettings } from "@/hooks/usePrintSettings";
import { TermViewDialog } from "@/components/terms/TermViewDialog";
import { TermShareDrawer } from "@/components/terms/TermShareDrawer";
import { SendForSignatureDialog } from "@/components/terms/SendForSignatureDialog";
import { deriveRequiredSignatories } from "@/lib/terms/signature-requirements";
import { getLatestPetWeight } from "@/lib/utils/pet-weight";
import type { Database } from "@/integrations/supabase/types";

type PetWeightRow = Database["public"]["Tables"]["pet_weights"]["Row"];

type PatientTermsPanelProps = {
  petId: string;
  clinicId?: string | null;
  medicalRecordId?: string | null;
  title?: string;
  description?: string;
  hideExistingTerms?: boolean;
  /** When true, only list terms; hide creation form and mutating actions (edit, delete, upload). */
  readOnly?: boolean;
  /** Called after a term is successfully saved and printed (e.g. to close a parent modal). */
  onCloseRequest?: () => void;
  /** Called when a draft term is created (e.g. during consultation creation) so the parent can link it on save. */
  onDraftCreatedId?: (id: string) => void;
  /** Called when a draft term is deleted so the parent can stop tracking it. */
  onDraftDeletedId?: (id: string) => void;
  variant?: "desktop" | "mobile";
  /** When provided, the primary action button (Adicionar termo / Salvar e imprimir) is portaled here instead of rendered inline. Used by modal callers that render their own fixed footer. */
  footerContainer?: HTMLDivElement | null;
  /** Fires whenever the "content shown" state (a template is selected) toggles, so a parent modal can resize itself. */
  onExpandedChange?: (expanded: boolean) => void;
  /** Ref to the "Modelo" autocomplete input, so a parent modal can focus it on open. */
  modelInputRef?: React.Ref<HTMLInputElement>;
};

type PetOwnerRow = {
  name: string;
  record_number: string | null;
  birth_date: string | null;
  gender: { name: string } | null;
  species: { name: string } | null;
  breed: { name: string } | null;
  weights: PetWeightRow[] | null;
  owner: {
    id: string;
    name: string;
    document: string | null;
    document_type_id: string | null;
    gender_id: string | null;
    birth_date: string | null;
    profession: string | null;
    marketing_consent: boolean;
    lead_source_id: string | null;
    owners_phones: Array<{ phone_number: string; is_whatsapp: boolean | null; is_primary: boolean | null; deleted_at: string | null }> | null;
    owners_emails: Array<{ email: string; is_primary: boolean | null; deleted_at: string | null }> | null;
    owners_addresses: Array<{
      street: string | null;
      number: string | null;
      neighborhood: string | null;
      complement: string | null;
      zip_code: string | null;
      city_id: string | null;
      is_primary: boolean | null;
      deleted_at: string | null;
    }> | null;
  } | null;
};

type VetProfileRow = {
  id: string;
  full_name: string;
  cpf: string | null;
  crmv_state: string | null;
  crmv_number: number | null;
  mapa_number: string | null;
  email: string | null;
  current_clinic_id: string | null;
};

type ClinicRow = {
  id: string;
  name: string;
  cnpj: string | null;
};

type TermWorkflowStage = "saving" | "preparing" | "printing" | "updatingHistory";

type QueuedPrintDoc = {
  termId?: string;
  title: string;
  content: string;
  markPrintedOnSuccess?: boolean;
};

const formatDate = (value?: string | null) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return format(parsed, "dd/MM/yyyy", { locale: ptBR });
};

const formatWeight = (value?: number | null) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "";
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(value);
};

const formatAddress = (address?: {
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  complement?: string | null;
  zip_code?: string | null;
} | null) => {
  if (!address) return "";

  const firstLine = [address.street, address.number].filter(Boolean).join(", ");
  const secondLine = [address.neighborhood, address.complement].filter(Boolean).join(" - ");
  const zip = address.zip_code ? `CEP: ${address.zip_code}` : "";
  return [firstLine, secondLine, zip].filter(Boolean).join(" | ");
};

const pickPrimary = <T extends { is_primary: boolean | null; deleted_at: string | null }>(rows?: T[] | null) => {
  const filtered = (rows ?? []).filter((row) => !row.deleted_at);
  return filtered.find((row) => row.is_primary) ?? filtered[0] ?? null;
};

const asStringRecord = (value: unknown) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {} as Record<string, string>;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, rowValue]) => [key, typeof rowValue === "string" ? rowValue : ""]),
  );
};

/** Prevents duplicate term creation for the same pet (e.g. double-click or multiple panel instances). */
const creatingPetIds = new Set<string>();

const PATIENT_TERMS_PET_OWNER_SELECT = `
  name,record_number,birth_date,
  gender:pet_genders(name),
  species:species(name),
  breed:breeds(name),
  weights:pet_weights(*),
  owner:owners(
    id,name,document,document_type_id,gender_id,birth_date,profession,marketing_consent,lead_source_id,
    owners_phones(phone_number,is_whatsapp,is_primary,deleted_at),
    owners_emails(email,is_primary,deleted_at),
    owners_addresses(street,number,neighborhood,complement,zip_code,city_id,is_primary,deleted_at)
  )
`;

export const PatientTermsPanel = ({
  petId,
  clinicId,
  medicalRecordId,
  title = "Termos do paciente",
  readOnly = false,
  onCloseRequest,
  onDraftCreatedId,
  variant = "desktop",
  footerContainer = null,
  onExpandedChange,
  modelInputRef,
}: PatientTermsPanelProps) => {
  const { t } = useTranslation("terms");
  const { data: printSettings } = usePrintSettings();
  const { data: a1Certificate } = useUserA1Certificate();
  const hasCertificate = Boolean(a1Certificate);
  const { data: isVeterinarian } = useIsVeterinarian();
  const signPdf = useSignPdf();
  const [signingTermId, setSigningTermId] = useState<string | null>(null);
  const [digitallySignedPaths, setDigitallySignedPaths] = useState<Record<string, string>>({});
  const renderPdf = useRenderPdf();

  const { hasPermission: canView, isLoading: isLoadingViewPermission } = useHasPermissionSync("terms", "view");
  const { hasPermission: canCreate, isLoading: isLoadingCreatePermission } = useHasPermissionSync("terms", "create");
  const { hasPermission: canUpdate, isLoading: isLoadingUpdatePermission } = useHasPermissionSync("terms", "edit");
  const isPermissionLoading =
    isLoadingViewPermission ||
    isLoadingCreatePermission ||
    isLoadingUpdatePermission;

  const { data: documentTypes = [] } = useDocumentTypes();
  const { data: templates = [], isLoading: isLoadingTemplates } = useTermTemplates(true, clinicId ?? null);

  const createTerm = useCreatePatientTerm();
  const markPrinted = useMarkTermPrinted();
  const uploadSignedFile = useUploadTermSignedFile();

  const [selectedModelId, setSelectedModelId] = useState("");
  const [modelSearchValue, setModelSearchValue] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [newTermContent, setNewTermContent] = useState("");
  const [isSaveAndPrintInProgress, setIsSaveAndPrintInProgress] = useState(false);
  const [termWorkflowStage, setTermWorkflowStage] = useState<TermWorkflowStage | null>(null);
  const [printFallbackDoc, setPrintFallbackDoc] = useState<QueuedPrintDoc | null>(null);
  const createInProgressRef = useRef(false);

  // Term view dialog state (matches history timeline pattern)
  const [selectedViewTerm, setSelectedViewTerm] = useState<PatientTerm | null>(null);
  const [isTermViewOpen, setIsTermViewOpen] = useState(false);
  const [isTermShareOpen, setIsTermShareOpen] = useState(false);
  const [isSendForSignatureOpen, setIsSendForSignatureOpen] = useState(false);

  const selectedModel = useMemo(
    () => templates.find((model) => model.id === selectedModelId) ?? null,
    [templates, selectedModelId],
  );

  useEffect(() => {
    onExpandedChange?.(Boolean(selectedModel));
  }, [selectedModel, onExpandedChange]);

  const modelOptions = useMemo<AutocompleteOption[]>(
    () => templates.map((model) => ({ value: model.id, label: model.name })),
    [templates],
  );

  const { data: contextData, isLoading: isLoadingContextData } = useQuery({
    queryKey: ["patient-terms", "token-context", petId, clinicId],
    queryFn: async () => {
      if (!petId) return null;

      const { data: petData, error: petError } = await supabase
        .from("pets")
        .select(PATIENT_TERMS_PET_OWNER_SELECT)
        .eq("id", petId)
        .single();

      if (petError) throw petError;

      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;

      let profile: VetProfileRow | null = null;
      let clinic: ClinicRow | null = null;

      if (userId) {
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("id,full_name,cpf,crmv_state,crmv_number,mapa_number,email,current_clinic_id")
          .eq("id", userId)
          .single();

        if (profileError) throw profileError;
        profile = profileData as VetProfileRow;

        const selectedClinicId = clinicId ?? profile.current_clinic_id;
        if (selectedClinicId) {
          const { data: clinicData, error: clinicError } = await supabase
            .from("clinics")
            .select("id,name,cnpj")
            .eq("id", selectedClinicId)
            .single();

          if (clinicError) throw clinicError;
          clinic = clinicData as ClinicRow;
        }
      }

      return {
        pet: (petData ?? null) as unknown as PetOwnerRow | null,
        profile,
        clinic,
        // profiles.email isn't reliably kept in sync — the auth session's e-mail is
        // the one actually used to log in and receive Resend e-mails.
        authEmail: authData.user?.email ?? null,
      };
    },
    enabled: Boolean(petId),
    staleTime: 1000 * 60 * 5,
  });
  const petOwnerData = contextData?.pet ?? null;

  const tokenValues = useMemo(() => {
    const petData = petOwnerData;
    const owner = petData?.owner;
    const latestWeight = getLatestPetWeight(petData?.weights ?? []);

    const ownerDocumentTypeName = owner?.document_type_id
      ? documentTypes.find((type) => type.id === owner.document_type_id)?.name ?? null
      : null;

    const rawOwnerDocument = owner?.document?.trim() ?? "";
    const ownerDocument =
      rawOwnerDocument && ownerDocumentTypeName
        ? `${ownerDocumentTypeName}: ${rawOwnerDocument}`
        : rawOwnerDocument;

    // Only prefill the signature dialog's CPF field when the owner's stored document
    // is actually a CPF — RESPONSAVEL_DOCUMENTO above (used for template-token
    // substitution in the printed term text) keeps the "<Type>: <value>" label format
    // regardless of type.
    const ownerRawCpf = ownerDocumentTypeName?.toUpperCase() === "CPF" ? rawOwnerDocument : "";

    const ownerPrimaryPhone = pickPrimary(owner?.owners_phones)?.phone_number ?? "";
    const ownerPrimaryEmail = pickPrimary(owner?.owners_emails)?.email ?? "";
    const ownerPrimaryAddress = formatAddress(pickPrimary(owner?.owners_addresses));

    const profile = contextData?.profile;
    const clinic = contextData?.clinic;

    const crmvState = profile?.crmv_state?.trim() ?? "";
    const crmvNumber = profile?.crmv_number ? String(profile.crmv_number) : "";
    const crmv = crmvState && crmvNumber ? `${crmvState}-${crmvNumber}` : crmvNumber;

    return {
      ANIMAL_NOME: petData?.name ?? "",
      ANIMAL_FICHA: petData?.record_number ?? "",
      ANIMAL_SEXO: petData?.gender?.name ?? "",
      ANIMAL_NASCIMENTO: formatDate(petData?.birth_date ?? null),
      ANIMAL_RACA: petData?.breed?.name ?? "",
      ANIMAL_ESPECIE: petData?.species?.name ?? "",
      ANIMAL_PESO: formatWeight(latestWeight?.weight),
      RESPONSAVEL_NOME: owner?.name ?? "",
      RESPONSAVEL_DOCUMENTO: ownerDocument,
      RESPONSAVEL_CPF_BRUTO: ownerRawCpf,
      RESPONSAVEL_TELEFONE: ownerPrimaryPhone,
      RESPONSAVEL_EMAIL: ownerPrimaryEmail,
      RESPONSAVEL_ENDERECO: ownerPrimaryAddress,
      CLINICA_NOME: clinic?.name ?? "",
      CLINICA_DOCUMENTO: clinic?.cnpj ?? "",
      CLINICA_TELEFONE: "",
      CLINICA_ENDERECO: "",
      USUARIO_NOME: profile?.full_name ?? "",
      USUARIO_CRMV: crmv,
      USUARIO_DOCUMENTO: profile?.cpf ?? "",
      DATA_ATUAL: format(new Date(), "dd/MM/yyyy", { locale: ptBR }),
      DATA_HORA_ATUAL: format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR }),
      ALERTA_GERAL: "",
      ALERTA_VACINAS: "",
    };
  }, [contextData, documentTypes, petOwnerData]);

  const ownerProfileForSignature = useMemo(() => {
    const owner = petOwnerData?.owner;
    if (!owner) return null;
    return {
      id: owner.id,
      name: owner.name,
      gender_id: owner.gender_id,
      birth_date: owner.birth_date,
      profession: owner.profession,
      marketing_consent: owner.marketing_consent,
      lead_source_id: owner.lead_source_id,
      phones: (owner.owners_phones ?? [])
        .filter((p) => p.deleted_at == null)
        .map((p) => ({
          phone_number: p.phone_number,
          is_whatsapp: p.is_whatsapp ?? false,
          is_primary: p.is_primary ?? false,
        })),
      addresses: (owner.owners_addresses ?? [])
        .filter((a) => a.deleted_at == null)
        .map((a) => ({
          zip_code: a.zip_code ?? "",
          street: a.street ?? "",
          number: a.number ?? "",
          complement: a.complement ?? "",
          neighborhood: a.neighborhood ?? "",
          city_id: a.city_id ?? "",
          is_primary: a.is_primary ?? false,
        })),
    };
  }, [petOwnerData]);

  const resolveTermTokens = useCallback(
    (input?: string | null) => {
      const freshValues = {
        ...tokenValues,
        ...buildSignatureTokenValues({
          vetName: tokenValues.USUARIO_NOME,
          vetCrmv: tokenValues.USUARIO_CRMV,
        }),
      };
      return sanitizeRichTextHtml(renderTermWithTokenValues(input ?? "", freshValues));
    },
    [tokenValues],
  );

  /** When user selects a model (or token context loads), fill the editable term content with the template. */
  useEffect(() => {
    if (selectedModel) {
      setNewTermContent(resolveTermTokens(selectedModel.content ?? ""));
    } else {
      setNewTermContent("");
    }
  }, [selectedModel, resolveTermTokens]);

  const isSubmittingCreate = createTerm.isPending || uploadSignedFile.isPending;

  const handleSignTermDigitally = async (term: PatientTerm) => {
    if (signingTermId) return;
    setSigningTermId(term.id);
    try {
      const result = await signPdf.mutateAsync({
        resource_type: "patient_term",
        resource_id: term.id,
        term_pet_id: petId,
      });

      setDigitallySignedPaths((prev) => ({ ...prev, [term.id]: result.storage_path }));
      toast.success("Termo assinado digitalmente!");
    } catch (error) {
      console.error("Erro ao assinar termo digitalmente:", error);
    } finally {
      setSigningTermId(null);
    }
  };

  const handleDownloadSignedTerm = async (term: PatientTerm) => {
    const path = digitallySignedPaths[term.id] ?? term.digitally_signed_storage_path;
    if (!path) return;
    try {
      const bucket = term.digitally_signed_storage_bucket ?? "signed-pdfs";
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 60);
      if (error || !data?.signedUrl) throw error ?? new Error("url_failed");
      const response = await fetch(data.signedUrl);
      if (!response.ok) throw new Error("download_failed");
      const bytes = new Uint8Array(await response.arrayBuffer());
      downloadPdf(bytes, `termo_assinado_${term.id.slice(0, 8)}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao baixar PDF assinado");
    }
  };

  const handleModelChange = (modelId: string) => {
    setSelectedModelId(modelId);
    const model = templates.find((item) => item.id === modelId);
    if (model?.name) {
      setTitleDraft(model.name);
      setModelSearchValue(model.name);
      return;
    }
    setModelSearchValue("");
  };

  const openTermView = (term: PatientTerm) => {
    setSelectedViewTerm(term);
    setIsTermViewOpen(true);
  };

  const openPrintFallbackPreview = (doc: QueuedPrintDoc) => {
    if (!doc.termId) return;

    const fallbackTerm: PatientTerm = {
      id: doc.termId,
      clinic_id: clinicId ?? null,
      pet_id: petId,
      medical_record_id: medicalRecordId ?? null,
      template_id: selectedModel?.id ?? null,
      title: doc.title,
      printed_at: null,
      signed_uploaded_at: null,
      signed_storage_bucket: null,
      signed_storage_path: null,
      signed_file_name: null,
      signed_mime_type: null,
      signed_file_size: null,
      rendered_title: doc.title,
      rendered_content: doc.content,
      field_values_json: null,
      resolved_values_json: tokenValues,
      template_snapshot_name: selectedModel?.name ?? "",
      template_snapshot_content: doc.content,
      template_snapshot_config: selectedModel ? normalizeTermTemplateConfig(selectedModel.config_json) : null,
      printed_by: null,
      created_by: "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      deleted_at: null,
      template: selectedModel ? { id: selectedModel.id, name: selectedModel.name } : null,
    };

    setSelectedViewTerm(fallbackTerm);
    setIsTermViewOpen(true);
  };

  const renderedViewTermContent = useMemo(() => {
    if (!selectedViewTerm) return null;
    const modelSource = templates.find((t) => t.id === selectedViewTerm.template_id) ?? null;
    const title = selectedViewTerm.rendered_title?.trim() || selectedViewTerm.title;
    const content =
      selectedViewTerm.rendered_content?.trim() ||
      (selectedViewTerm.template_snapshot_content?.trim()
        ? renderTermWithTokenValues(selectedViewTerm.template_snapshot_content, tokenValues)
        : modelSource?.content
          ? renderTermWithTokenValues(modelSource.content, tokenValues)
          : "");
    if (!title && !content) return null;
    return { title, content };
  }, [selectedViewTerm, templates, tokenValues]);

  const handleCreateTerm = async () => {
    if (createInProgressRef.current) return;
    if (creatingPetIds.has(petId)) return;

    createInProgressRef.current = true;
    creatingPetIds.add(petId);

    if (!canCreate) {
      toast.error("Você não tem permissão para criar termos.");
      createInProgressRef.current = false;
      creatingPetIds.delete(petId);
      return;
    }
    if (!clinicId) {
      toast.error(t("panel.noClinicSelected"));
      createInProgressRef.current = false;
      creatingPetIds.delete(petId);
      return;
    }

    const fallback = selectedModel?.name?.trim() ?? "";
    const finalTitle = titleDraft.trim() || fallback;

    if (!finalTitle) {
      toast.error("Defina um título ou selecione modelo para gerar o título automático.");
      createInProgressRef.current = false;
      creatingPetIds.delete(petId);
      return;
    }

    try {

      const renderedTitle = resolveTermTokens(finalTitle);
      const renderedContent = selectedModel
        ? resolveTermTokens(selectedModel.content ?? "")
        : "";

      const created = await createTerm.mutateAsync({
        clinic_id: clinicId ?? "",
        pet_id: petId,
        medical_record_id: medicalRecordId ?? null,
        template_id: selectedModel?.id ?? null,
        title: finalTitle,
        resolved_values_json: tokenValues,
        template_snapshot_name: selectedModel?.name ?? "",
        template_snapshot_content: selectedModel?.content ?? "",
        template_snapshot_config: selectedModel ? normalizeTermTemplateConfig(selectedModel.config_json) : undefined,
        rendered_title: renderedTitle,
        rendered_content: renderedContent,
      });

      onDraftCreatedId?.(created.id);
      setSelectedModelId("");
      setModelSearchValue("");
      setTitleDraft("");
    } finally {
      createInProgressRef.current = false;
      creatingPetIds.delete(petId);
    }
  };

  const runPrintWorkflow = async (doc: QueuedPrintDoc) => {
    if (!doc.termId) {
      toast.error(t("panel.printFailed"));
      return;
    }
    const startedAt = performance.now();
    setTermWorkflowStage("preparing");

    try {
      console.info("[terms] print-workflow:start", {
        termId: doc.termId ?? null,
        petId,
        clinicId: clinicId ?? null,
        medicalRecordId: medicalRecordId ?? null,
      });
      setTermWorkflowStage("printing");
      // Only saved terms are printed, from the PDF the Lambda renders.
      const bytes = await renderPdf.mutateAsync({ resource_type: "patient_term", resource_id: doc.termId });
      printPdf(bytes);

      if (doc.termId && doc.markPrintedOnSuccess) {
        setTermWorkflowStage("updatingHistory");
        await markPrinted.mutateAsync({ termId: doc.termId });
      }

      setPrintFallbackDoc(null);
      console.info("[terms] print-workflow:success", {
        termId: doc.termId ?? null,
        durationMs: Math.round(performance.now() - startedAt),
      });
    } catch (error) {
      console.error("[terms] print-workflow:failed", {
        termId: doc.termId ?? null,
        petId,
        clinicId: clinicId ?? null,
        medicalRecordId: medicalRecordId ?? null,
        stage: termWorkflowStage ?? "preparing",
        error,
      });
      setPrintFallbackDoc(doc);
      toast.error(t("panel.printFailed"));
      throw error;
    } finally {
      setTermWorkflowStage(null);
    }
  };

  /** Create term from selected model with edited content, print, and reset — all in the first modal (no second modal). */
  const handleSaveAndPrint = async () => {
    if (!selectedModel) return;
    if (createInProgressRef.current) return;
    if (creatingPetIds.has(petId)) return;

    createInProgressRef.current = true;
    creatingPetIds.add(petId);
    setIsSaveAndPrintInProgress(true);

    if (!canCreate) {
      toast.error("Você não tem permissão para criar termos.");
      createInProgressRef.current = false;
      creatingPetIds.delete(petId);
      setIsSaveAndPrintInProgress(false);
      return;
    }
    if (!clinicId) {
      toast.error(t("panel.noClinicSelected"));
      createInProgressRef.current = false;
      creatingPetIds.delete(petId);
      setIsSaveAndPrintInProgress(false);
      return;
    }

    const finalTitle = titleDraft.trim() || selectedModel.name?.trim() || "";
    if (!finalTitle) {
      toast.error("Defina um título para o termo.");
      createInProgressRef.current = false;
      creatingPetIds.delete(petId);
      setIsSaveAndPrintInProgress(false);
      return;
    }
    if (!newTermContent.trim()) {
      toast.error("O conteúdo do termo não pode estar vazio.");
      createInProgressRef.current = false;
      creatingPetIds.delete(petId);
      setIsSaveAndPrintInProgress(false);
      return;
    }

    const renderedTitle = resolveTermTokens(finalTitle);
    const renderedContent = resolveTermTokens(newTermContent);
    try {
      setTermWorkflowStage("saving");
      const created = await createTerm.mutateAsync({
        clinic_id: clinicId ?? "",
        pet_id: petId,
        medical_record_id: medicalRecordId ?? null,
        template_id: selectedModel.id,
        title: finalTitle,
        resolved_values_json: tokenValues,
        template_snapshot_name: selectedModel.name ?? "",
        template_snapshot_content: selectedModel.content ?? "",
        template_snapshot_config: normalizeTermTemplateConfig(selectedModel.config_json),
        rendered_title: renderedTitle,
        rendered_content: renderedContent,
      });

      onDraftCreatedId?.(created.id);
      setSelectedModelId("");
      setModelSearchValue("");
      setTitleDraft("");
      setNewTermContent("");
      // Keep the surrounding panel/dialog mounted (don't call onCloseRequest) so the
      // term details dialog opened below stays visible instead of being unmounted
      // along with it.
      openTermView(created);
    } finally {
      createInProgressRef.current = false;
      creatingPetIds.delete(petId);
      setIsSaveAndPrintInProgress(false);
      setTermWorkflowStage(null);
    }
  };

  const handleUploadSignedOnExisting = async (term: PatientTerm, file: File | null) => {
    if (!file || !clinicId) return;
    if (!canUpdate) {
      toast.error("Você não tem permissão para atualizar termos.");
      return;
    }

    await uploadSignedFile.mutateAsync({
      termId: term.id,
      petId,
      clinicId,
      file,
    });
  };

  const handleOpenSignedFile = async (term: PatientTerm) => {
    if (!term.signed_storage_bucket || !term.signed_storage_path) {
      toast.error("Este termo não possui arquivo assinado.");
      return;
    }

    const { data, error } = await supabase.storage
      .from(term.signed_storage_bucket)
      .createSignedUrl(term.signed_storage_path, 3600);

    if (error || !data?.signedUrl) {
      toast.error("Não foi possível abrir o arquivo.");
      return;
    }

    window.open(data.signedUrl, "_blank");
  };

  if (!isPermissionLoading && !canView) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>Você não possui permissão para visualizar termos.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className={cn("space-y-6", variant === "mobile" ? "p-4" : "mt-2")}>
        {!readOnly && (
          <>
            <div className="space-y-3 rounded-lg border p-4 ">
              <div className="grid gap-3">
                <div className="space-y-2">
                  <Label>Modelo</Label>
                  <Autocomplete
                    ref={modelInputRef}
                    options={[{ value: "", label: "Sem modelo" }, ...modelOptions]}
                    value={modelSearchValue}
                    onChange={(value) => {
                      setModelSearchValue(value);
                      if (!value.trim() || value !== (selectedModel?.name ?? "")) {
                        setSelectedModelId("");
                      }
                    }}
                    onSelect={(option) => handleModelChange(option.value)}
                    placeholder={t("panel.searchModelPlaceholder")}
                    emptyMessage="Nenhum modelo encontrado."
                    className="h-10"
                    popoverPortalled={false}
                    listClassName="max-h-36"
                    disabled={!canCreate || isSubmittingCreate || isLoadingTemplates}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="patient-term-title">Título</Label>
                  <Input
                    id="patient-term-title"
                    placeholder={t("panel.titlePlaceholder")}
                    value={titleDraft}
                    onChange={(event) => setTitleDraft(event.target.value)}
                    disabled={!canCreate || isSubmittingCreate}
                  />
                </div>
              </div>
            </div>

            {selectedModel ? (() => {
              const saveAndPrintButton = (
                <Button
                  type="button"
                  className={cn(!footerContainer && "w-full")}
                  disabled={
                    !canCreate ||
                    isSubmittingCreate ||
                    markPrinted.isPending ||
                    isPermissionLoading ||
                    isLoadingContextData ||
                    !newTermContent.trim() ||
                    isSaveAndPrintInProgress
                  }
                  onClick={() => void handleSaveAndPrint()}
                >
                  {isSubmittingCreate || isSaveAndPrintInProgress ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("panel.status.saving")}
                    </>
                  ) : (
                    <>
                      <Eye className="mr-2 h-4 w-4" />
                      {t("panel.saveAndPrint")}
                    </>
                  )}
                </Button>
              );

              return (
                <>
                  <div className="space-y-2 rounded-lg border p-4">
                    <Label>Conteúdo do termo</Label>
                    {isLoadingContextData ? (
                      <div className="flex min-h-[320px] items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <RichTextEditor
                        value={newTermContent}
                        onChange={setNewTermContent}
                        minHeightClassName="min-h-[320px]"
                      />
                    )}
                  </div>
                  {footerContainer
                    ? createPortal(saveAndPrintButton, footerContainer)
                    : <div className="flex flex-col gap-2 sm:flex-row">{saveAndPrintButton}</div>}
                </>
              );
            })() : (() => {
              const addTermButton = (
                <Button
                  type="button"
                  className={cn(!footerContainer && "w-full")}
                  disabled={
                    !canCreate ||
                    isSubmittingCreate ||
                    isPermissionLoading ||
                    (Boolean(selectedModelId) && isLoadingContextData)
                  }
                  onClick={handleCreateTerm}
                >
                  {isSubmittingCreate ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("panel.status.saving")}
                    </>
                  ) : (
                    <>
                      <FilePlus2 className="mr-2 h-4 w-4" />
                      {t("panel.addTerm")}
                    </>
                  )}
                </Button>
              );

              return footerContainer
                ? createPortal(addTermButton, footerContainer)
                : <div className="flex flex-col gap-2 sm:flex-row">{addTermButton}</div>;
            })()}

            {printFallbackDoc ? (
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => void runPrintWorkflow(printFallbackDoc)}
                  disabled={Boolean(termWorkflowStage)}
                >
                  <Printer className="mr-2 h-4 w-4" />
                  {t("panel.retryPrint")}
                </Button>
                {printFallbackDoc.termId ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => openPrintFallbackPreview(printFallbackDoc)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    {t("panel.openPreview")}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </>
        )}

      {/* Term View Dialog */}
      {selectedViewTerm && (() => {
        const isDigitallySigned = Boolean(
          digitallySignedPaths[selectedViewTerm.id] ??
          selectedViewTerm.digitally_signed_at ??
          selectedViewTerm.digitally_signed_storage_path,
        );
        const requiredRoles = deriveRequiredSignatories(
          selectedViewTerm.template_snapshot_content?.trim() || selectedViewTerm.rendered_content || "",
        );
        const requiresResponsible = requiredRoles.includes("responsible");
        const requiresVet = requiredRoles.includes("vet");
        const vetBlockedReason =
          !readOnly && requiresVet
            ? isVeterinarian === false
              ? t("signature.send.vetConfirm.notVeterinarian")
              : !tokenValues.USUARIO_CRMV
                ? t("signature.send.vetConfirm.missingCrmv")
                : null
            : null;
        return (
          <TermViewDialog
            open={isTermViewOpen}
            onOpenChange={(open) => {
              setIsTermViewOpen(open);
              // A user-driven close (X / escape / overlay) closes the whole stack,
              // including the wrapping "Termos do paciente" dialog. This only fires on
              // real interaction — programmatically hiding the dialog to open a child
              // (send/share) sets `open={false}` without invoking onOpenChange.
              if (!open) {
                setSelectedViewTerm(null);
                onCloseRequest?.();
              }
            }}
            term={selectedViewTerm}
            renderedContent={renderedViewTermContent}
            printSettings={printSettings}
            isDigitallySigned={isDigitallySigned}
            hasCertificate={hasCertificate}
            isSigningTerm={signingTermId === selectedViewTerm.id}
            signPdfPending={signPdf.isPending}
            onSign={() => void handleSignTermDigitally(selectedViewTerm)}
            onDownloadSigned={() => void handleDownloadSignedTerm(selectedViewTerm)}
            hasStoredFile={Boolean(selectedViewTerm.signed_storage_bucket && selectedViewTerm.signed_storage_path)}
            isUploadPending={uploadSignedFile.isPending}
            onViewStoredFile={() => void handleOpenSignedFile(selectedViewTerm)}
            onUploadFile={(file) => void handleUploadSignedOnExisting(selectedViewTerm, file)}
            canUpload={Boolean(clinicId) && !readOnly}
            onPrinted={async () => {
              await markPrinted.mutateAsync({ termId: selectedViewTerm.id });
            }}
            isPrintPending={markPrinted.isPending}
            onShare={
              isDigitallySigned
                ? () => {
                    // Opening a child modal closes the term-details dialog beneath it
                    // (keep `selectedViewTerm` so the child stays mounted).
                    setIsTermViewOpen(false);
                    setIsTermShareOpen(true);
                  }
                : undefined
            }
            onSendForSignature={
              !readOnly && requiresResponsible && !vetBlockedReason
                ? () => {
                    setIsTermViewOpen(false);
                    setIsSendForSignatureOpen(true);
                  }
                : undefined
            }
            sendForSignatureDisabledReason={
              !readOnly && requiresResponsible && vetBlockedReason ? vetBlockedReason : undefined
            }
          />
        );
      })()}

      {selectedViewTerm && (
        <TermShareDrawer
          open={isTermShareOpen}
          onOpenChange={(open) => {
            setIsTermShareOpen(open);
            // Closing the child modal closes the whole stack: the term-details
            // dialog beneath it (already hidden when this opened) and the wrapping
            // "Termos do paciente" dialog, if any.
            if (!open) {
              setSelectedViewTerm(null);
              onCloseRequest?.();
            }
          }}
          term={
            selectedViewTerm as PatientTerm & {
              digitally_signed_access_code?: string | null;
            }
          }
          ownerName={petOwnerData?.owner?.name ?? null}
          ownerPhones={
            (petOwnerData?.owner?.owners_phones ?? []).filter(
              (p) => p.deleted_at == null && p.phone_number,
            ) as Array<{ phone_number: string; is_primary: boolean | null; deleted_at: string | null }>
          }
          petName={petOwnerData?.name ?? null}
        />
      )}

      {selectedViewTerm && (
        <SendForSignatureDialog
          open={isSendForSignatureOpen}
          onOpenChange={(open) => {
            setIsSendForSignatureOpen(open);
            // After sending (or cancelling) the whole modal stack closes — including
            // the wrapping "Termos do paciente" dialog, if any.
            if (!open) {
              setSelectedViewTerm(null);
              onCloseRequest?.();
            }
          }}
          term={selectedViewTerm}
          petId={petId}
          vetName={tokenValues.USUARIO_NOME ?? ""}
          hasCertificate={hasCertificate}
          requiresVetSignature={deriveRequiredSignatories(
            selectedViewTerm.template_snapshot_content?.trim() || selectedViewTerm.rendered_content || "",
          ).includes("vet")}
          vetEmail={contextData?.authEmail ?? contextData?.profile?.email ?? ""}
          vetCrmv={tokenValues.USUARIO_CRMV || undefined}
          vetMapa={contextData?.profile?.mapa_number ?? undefined}
          prefill={{
            name: tokenValues.RESPONSAVEL_NOME || (petOwnerData?.owner?.name ?? ""),
            cpf: tokenValues.RESPONSAVEL_CPF_BRUTO ?? "",
            email: tokenValues.RESPONSAVEL_EMAIL ?? "",
            phone: tokenValues.RESPONSAVEL_TELEFONE ?? "",
          }}
          ownerId={petOwnerData?.owner?.id ?? null}
          ownerProfile={ownerProfileForSignature}
          documentTypes={documentTypes}
        />
      )}
    </div>
  );
};
